import React, { useEffect, useState, useCallback } from "react";
import api from "../api";

/**
 * Anonymous review section for generated product pages.
 * - Shows star rating + content only (no author / date).
 * - Anonymous submission allowed (backend allows user_id NULL for product_type="page").
 */
export default function ReviewSection({ productType = "page", productId, accent = "sale" }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const accentColor = accent === "rent" ? "#1fb683" : "#0a6bff";

  const load = useCallback(async () => {
    if (!productId) return;
    try {
      const { data } = await api.get(`/api/reviews/${productType}/${productId}`);
      const all = [
        ...(data.user_reviews || []),
        ...(data.crawled || []),
      ];
      setReviews(all);
    } catch (_) {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [productType, productId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (content.trim().length < 2) {
      alert("리뷰 내용을 입력하세요");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/api/reviews/${productType}/${productId}`, {
        rating: Number(rating),
        content: content.trim(),
      });
      setContent("");
      setRating(5);
      setShowForm(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || "리뷰 등록 실패");
    } finally {
      setSubmitting(false);
    }
  };

  const avg = reviews.length > 0
    ? (reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length).toFixed(1)
    : "0.0";

  return (
    <section className="py-12 md:py-16 bg-white border-t border-gray-100">
      <div className="max-w-[900px] mx-auto px-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[11px] font-semibold tracking-widest uppercase mb-1" style={{ color: accentColor }}>
              REVIEWS
            </div>
            <h2 className="text-[22px] sm:text-[26px] font-bold text-[#1d1d1f]">
              사용자 리뷰
              <span className="ml-3 text-[15px] font-medium text-[#86868b]">
                <Stars value={Math.round(Number(avg))} size={14} /> {avg} ({reviews.length})
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-5 py-2.5 rounded-full text-[13px] font-semibold text-white transition hover:opacity-90"
            style={{ background: accentColor }}
          >
            {showForm ? "닫기" : "리뷰 작성"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-8 p-5 rounded-2xl bg-[#f5f5f7]">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-[12px] text-[#86868b] mr-1">별점</span>
              <StarPicker value={rating} onChange={setRating} color={accentColor} />
              <span className="text-[12px] font-semibold text-[#1d1d1f]">{rating}.0</span>
            </div>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="리뷰 내용을 입력하세요 (익명으로 등록됩니다)"
              className="w-full p-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:border-gray-400 resize-none bg-white"
              required
            />
            <div className="flex justify-end mt-3 gap-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setContent(""); }}
                className="px-4 py-2 rounded-full text-[12px] font-medium text-[#1d1d1f] bg-white border border-gray-200 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 rounded-full text-[12px] font-semibold text-white disabled:opacity-50"
                style={{ background: accentColor }}
              >
                {submitting ? "등록 중..." : "등록"}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-center py-8 text-[13px] text-[#86868b]">불러오는 중...</div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-10 text-[13px] text-[#86868b]">
            아직 등록된 리뷰가 없습니다. 첫 리뷰를 남겨주세요.
          </div>
        ) : (
          <ul className="space-y-3">
            {reviews.map((r, i) => (
              <li key={i} className="p-4 rounded-2xl bg-[#fafafa] border border-gray-100">
                <div className="mb-2">
                  <Stars value={Number(r.rating) || 0} size={13} />
                </div>
                <p className="text-[13px] leading-relaxed text-[#1d1d1f] whitespace-pre-wrap">
                  {r.content}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Stars({ value = 0, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5 align-middle">
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={n <= value ? "#ffb400" : "#e5e7eb"}
        >
          <path d="M12 2l2.95 6.97L22 9.97l-5.5 4.86L18.18 22 12 18.27 5.82 22l1.68-7.17L2 9.97l7.05-1z" />
        </svg>
      ))}
    </span>
  );
}

function StarPicker({ value, onChange, color = "#0a6bff" }) {
  return (
    <span className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="transition hover:scale-110"
          aria-label={`${n}점`}
        >
          <svg width={22} height={22} viewBox="0 0 24 24" fill={n <= value ? color : "#e5e7eb"}>
            <path d="M12 2l2.95 6.97L22 9.97l-5.5 4.86L18.18 22 12 18.27 5.82 22l1.68-7.17L2 9.97l7.05-1z" />
          </svg>
        </button>
      ))}
    </span>
  );
}
